import React, { useEffect, useState } from "react";
import dayjs from "dayjs";
import { Radio, RadioGroup, Space } from "@mantine/core";
import { DatePicker, TimeInput } from "@mantine/dates";

type ScheduleDateTimeInputProps = {
  // eslint-disable-next-line  @typescript-eslint/no-explicit-any
  form: any;
  description: string;
};
const ScheduleDateTimeInput: React.FC<ScheduleDateTimeInputProps> = ({
  form,
  description,
}) => {
  const today = new Date();

  const { scheduledEndDate, scheduledStartDate } = form.values;

  const selectedDate = scheduledStartDate || today;
  const dateRanges = [
    {
      value: "24h",
      label: "24h",
      time: dayjs(selectedDate).add(24, "hour").toDate(),
    },
    {
      value: "3d",
      label: "3 days",
      time: dayjs(selectedDate).add(3, "day").toDate(),
    },
    {
      value: "7d",
      label: "7 days",
      time: dayjs(selectedDate).add(7, "day").toDate(),
    },
    { value: "custom", label: "Custom", time: scheduledEndDate },
  ];

  const [dateRange, setDateRange] = useState(() => {
    // Check if dates match any preset exactly (not just by label string)
    // Only perform comparison if we have a valid end date
    if (!scheduledEndDate) {
      return "7d"; // Default to 7 days if no end date
    }

    const selected = dateRanges.find((it) => {
      if (it.value === "custom") return false; // Skip custom option

      // Compare the actual dates, not the human-readable string
      // Check if the end date matches what the preset would generate
      const expectedEndDate = it.time;
      const actualEndDate = scheduledEndDate;

      // Use dayjs to check if dates match (within same minute to account for seconds)
      return dayjs(actualEndDate).isSame(expectedEndDate, "minute");
    });

    if (selected) {
      return selected.value;
    }
    // If no preset matches, it's a custom date range
    return "custom";
  });
  const [showDatePick, setShowDatePick] = useState(
    () =>
      // Show date picker initially if we have a custom date range
      dateRange === "custom"
  );

  useEffect(() => {
    // Only update the date picker visibility
    setShowDatePick(dateRange === "custom");

    // Only update the end date if the user actively changed the selection
    // (not on initial render with existing custom dates)
    if (dateRange !== "custom") {
      let newEndDate;
      switch (dateRange) {
        case "3d":
          newEndDate = dateRanges[1].time;
          break;
        case "7d":
          newEndDate = dateRanges[2].time;
          break;
        default:
          newEndDate = dateRanges[0].time; // 24h
      }
      form.setFieldValue("scheduledEndDate", newEndDate);
    }
  }, [dateRange]);

  const onChangeTimeHandler = (date: Date, time: Date): Date =>
    dayjs(date)
      .set("hour", time.getHours())
      .set("minute", time.getMinutes())
      .set("second", 0)
      .toDate();

  return (
    <>
      <RadioGroup
        label="Date range"
        color="jem-theme"
        styles={{
          description: {
            fontSize: "12px",
            fontWeight: "normal",
            fontStretch: "normal",
            fontStyle: "normal",
            lineHeight: "1.33",
            letterSpacing: "0.16px",
            textAlign: "left",
          },
        }}
        description={description}
        size="xs"
        value={dateRange}
        spacing="sm"
        onChange={(value) => setDateRange(value)}
        required
        // {...form.getInputProps("dateRange")}
      >
        {dateRanges.map((it) => (
          <Radio key={`radio-${it.value}`} value={it.value} label={it.label} />
        ))}
      </RadioGroup>
      {showDatePick && (
        <>
          <Space h="xl" />
          <DatePicker
            color="jem-theme"
            excludeDate={(excludeDate) =>
              excludeDate < dayjs(today).subtract(1, "day").toDate()
            }
            placeholder="Pick send date"
            label="Start Date"
            {...form.getInputProps("scheduledStartDate")}
            required
          />
          <Space h="xl" />
          <TimeInput
            color="jem-theme"
            defaultValue={form.values.scheduledStartDate || undefined}
            label="Start Time"
            onChange={(time) => {
              if (!form.values.scheduledStartDate) return;
              const startDate = onChangeTimeHandler(
                form.values.scheduledStartDate,
                time
              );
              form.setFieldValue("scheduledStartDate", startDate);
            }}
          />
          <Space h="xl" />
          <DatePicker
            color="jem-theme"
            excludeDate={(excludeDate) => {
              if (!form.values.scheduledStartDate) {
                return excludeDate < dayjs(today).subtract(1, "day").toDate();
              }
              return (
                excludeDate < dayjs(today).subtract(1, "day").toDate() ||
                excludeDate < dayjs(form.values.scheduledStartDate).toDate()
              );
            }}
            placeholder="Pick end date"
            label="End Date"
            {...form.getInputProps("scheduledEndDate")}
            required
          />
          <Space h="xl" />
          <TimeInput
            color="jem-theme"
            defaultValue={form.values.scheduledEndDate || undefined}
            label="End Time"
            onChange={(time) => {
              if (!form.values.scheduledEndDate) return;
              const endDate = onChangeTimeHandler(
                form.values.scheduledEndDate,
                time
              );
              form.setFieldValue("scheduledEndDate", endDate);
            }}
          />
        </>
      )}
    </>
  );
};

export default ScheduleDateTimeInput;
